import { cn } from '@/lib/utils'
import type { LineItemMedia } from '@/lib/services/line-items/line-items.types'
import type { ComponentProps, ReactElement } from 'react'

const DEFAULT_CUSTOM_ITEM_ASSET_SUFFIXES = [
  '/default-custom-item-photo.svg',
  '/default-custom-item-photo.png',
]

function isDefaultCustomItemAssetUrl(url: LineItemMedia['url']): boolean {
  if (!url) {
    return false
  }

  const normalizedUrl = String(url).split('?')[0]

  return DEFAULT_CUSTOM_ITEM_ASSET_SUFFIXES.some(
    (suffix) => normalizedUrl === suffix || normalizedUrl?.endsWith(suffix)
  )
}

type ImageThumbnailProps = ComponentProps<'img'> & {
  media?: Pick<LineItemMedia, 'url' | 'name'> | null
}

const ImageThumbnail = ({ media, className = '', ...props }: ImageThumbnailProps): ReactElement => {
  const width = 'w-[80px]'
  const needsLightModeContrast = isDefaultCustomItemAssetUrl(media?.url)

  return (
    <div className={cn(width, 'shrink-0 relative rounded-lg overflow-hidden shadow-lg', className)}>
      <img
        src={media?.url ?? undefined}
        alt={media?.name ?? undefined}
        data-contrast-mode={needsLightModeContrast ? 'default-custom-item' : 'standard'}
        className={cn(
          'block h-full w-full object-cover',
          needsLightModeContrast &&
            'brightness-[0.45] contrast-125 dark:brightness-100 dark:contrast-100'
        )}
        {...props}
        loading="lazy"
      />
    </div>
  )
}

export default ImageThumbnail
